import { useNavigation } from "@react-navigation/native";
import { Text } from "@ui-kitten/components";
import BottomSheet from "components/BottomSheet/BottomSheet";
import Button from "components/Button";
import Chip from "components/Chip";
import MainContainer from "components/MainContainer";
import { useAccount } from "hooks/useAccount";
import i18n from "plugins/localization";
import { useFetchBookingRequestQuery } from "queries/bookingRequest.queries";
import React, { useState } from "react";
import { Pressable, ScrollView, View } from "react-native";
import { RefreshControl } from "react-native-web-refresh-control";
import BookingRequests from "./BookingRequests";
import EventListItem from "./EventListItem";

export default function MyEvents() {
  const { accountEvents: events = [], fetchAccount, account } = useAccount();
  const [refreshing, setRefreshing] = useState(false);
  const [selectedEventType, setSelectedEventType] = useState<string>("All");
  const [bottomSheetVisible, setBottomSheetVisible] = useState(false);

  const navigation = useNavigation() as any;

  const accountHasTicketApprovalPlans = Boolean(
    account?.subscriptions?.some(
      (sub) => sub.status === "ACTIVE" && sub.planPackage.ticketApprovalEnabled,
    ),
  );
  const { data: bookingRequests } = useFetchBookingRequestQuery(
    accountHasTicketApprovalPlans,
  );

  const onRefresh = async () => {
    setRefreshing(true);
    await fetchAccount();
    setRefreshing(false);
  };

  const availableEventTypes = [
    "All",
    ...new Set(events.map((event) => event.event.type)),
  ].sort((a, b) => a.localeCompare(b)); // sort by event type alphabetically

  const filteredEvents = (
    selectedEventType === "All"
      ? events
      : events.filter((event) => event.event.type === selectedEventType)
  ).sort((a, b) => {
    const dateDiff =
      new Date(a.event.date).getTime() - new Date(b.event.date).getTime();
    if (dateDiff !== 0) return dateDiff;
    const aIsParking = a.event.type === "PARKING" ? 1 : 0;
    const bIsParking = b.event.type === "PARKING" ? 1 : 0;
    return aIsParking - bIsParking;
  });

  const handleSeeRequestedTickets = () => {
    setBottomSheetVisible(true);
  };

  return (
    <MainContainer
      showLeftAction={false}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
      }
    >
      {/* because 'All' is always included, we need at least 3 items to show the scrollable chips */}
      {availableEventTypes.length > 2 && (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{
            paddingVertical: 10,
            paddingHorizontal: 10,
            gap: 10,
          }}
          style={{ flexGrow: 0 }}
        >
          {availableEventTypes.map((type) => (
            <Chip
              key={type}
              onPress={() => setSelectedEventType(type)}
              selected={selectedEventType === type}
              size="medium"
            >
              {type}
            </Chip>
          ))}
        </ScrollView>
      )}

      {accountHasTicketApprovalPlans && (
        <View className="self-end my-4">
          <Button onPress={handleSeeRequestedTickets}>
            {`My Booking Requests${
              bookingRequests?.length ? ` (${bookingRequests.length})` : ""
            }`}
          </Button>
        </View>
      )}

      {filteredEvents.length === 0 && (
        <Text
          style={{
            color: "gray",
            textAlign: "center",
            fontWeight: "700",
            marginTop: 10,
            fontSize: 28,
          }}
        >
          {i18n.t("screens.MyEvents.noGamesYet")}
        </Text>
      )}
      <View style={{ paddingHorizontal: 15 }}>
        {filteredEvents.map((eventDetail) => {
          return (
            <Pressable
              key={eventDetail.groupCode.groupCode}
              onPress={() =>
                navigation.push("EventDetail", {
                  groupCode: eventDetail.groupCode.groupCode,
                })
              }
            >
              <EventListItem eventDetail={eventDetail} />
            </Pressable>
          );
        })}
      </View>

      <BottomSheet
        visible={bottomSheetVisible}
        onClose={() => setBottomSheetVisible(false)}
        title="My Booking Requests"
      >
        {bookingRequests && (
          <BookingRequests bookingRequests={bookingRequests} />
        )}
      </BottomSheet>
    </MainContainer>
  );
}
